import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  TypingIndicator,
} from '@chatscope/chat-ui-kit-react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { useCollection } from 'react-firebase-hooks/firestore';
import { db } from '../firebase';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import Header from '../components/Header';
import './ChatScreen.css';

function ChatScreen({ userId }) {
  const { sessionId } = useParams();
  const [messages, setMessages] = useState([]);
  const [audioUrls, setAudioUrls] = useState({});
  const [voiceChatEnabled, setVoiceChatEnabled] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  
  // Estados para el streaming
  const [streamingMessages, setStreamingMessages] = useState({});
  const [processedMessageIds, setProcessedMessageIds] = useState(new Set());
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const messagesEndRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const inputRef = useRef(null);

  const messagesRef = sessionId
    ? collection(db, `session-messages/${sessionId}/messages`)
    : null;
  
  const q = messagesRef 
    ? query(messagesRef, orderBy('created_at', 'asc'))
    : null;

  const welcomeMessages = ["Si algo te pesa, podés soltarlo acá.", "¿Qué te anda molestando?", "Contame qué tenés en la cabeza.", "Hola, ¿cómo te sentís hoy?", "No tenés que guardarte todo. ¿Querés hablar?"];
  const [snapshot, loading] = useCollection(q);

  // Función para simular streaming de texto
  const simulateStreaming = async (messageId, fullText, onComplete) => {
    const words = fullText.split(' ');
    let currentText = '';
    
    for (let i = 0; i < words.length; i++) {
      currentText += (i > 0 ? ' ' : '') + words[i];
      setStreamingMessages(prev => ({
        ...prev,
        [messageId]: currentText
      }));
      
      // Pausa aleatoria entre palabras para simular escritura natural
      await new Promise(resolve => setTimeout(resolve, 50 + Math.random() * 100));
    }
    
    // Marcar como completado
    setProcessedMessageIds(prev => new Set([...prev, messageId]));
    setStreamingMessages(prev => {
      const newState = { ...prev };
      delete newState[messageId];
      return newState;
    });
    
    if (onComplete) onComplete();
  };

  // Scroll automático al final
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessages]);

  // Escuchar el documento messages/${sessionId} para obtener current_status
  useEffect(() => {
    if (!sessionId) return;

    const sessionDocRef = doc(db, 'session-messages', sessionId);
    
    const unsubscribe = onSnapshot(sessionDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const status = data.current_status;
        setCurrentStatus(status);
        
        // Controlar el estado del input según current_status
        if (status === 'system_typing') {
          setIsInputDisabled(true);
          setLocalLoading(false); // Desactivar loading local cuando el sistema empieza a escribir
        } else {
          setIsInputDisabled(false);
        }
      } else {
        // Si el documento no existe, current_status no existe
        setCurrentStatus(null);
        setIsInputDisabled(false);
      }
    }, (error) => {
      console.error('Error listening to session status:', error);
      setCurrentStatus(null);
      setIsInputDisabled(false);
    });

    return () => unsubscribe();
  }, [sessionId]);

  // Load audio URLs for audio messages
  useEffect(() => {
    const loadAudioUrls = async () => {
      const newAudioUrls = {};
      
      for (const message of messages) {
        if (message.message_type === 'audio' && message.audio_url && !audioUrls[message.id]) {
          try {
            if (message.audio_url.startsWith('http')) {
              newAudioUrls[message.id] = message.audio_url;
            } else {
              const storage = getStorage();
              const audioRef = ref(storage, message.audio_url);
              const url = await getDownloadURL(audioRef);
              newAudioUrls[message.id] = url;
            }
          } catch (error) {
            console.error('Error loading audio URL for message', message.id, ':', error);
          }
        }
      }
      
      if (Object.keys(newAudioUrls).length > 0) {
        setAudioUrls(prev => ({ ...prev, ...newAudioUrls }));
      }
    };

    loadAudioUrls();
  }, [messages, audioUrls]);

  React.useEffect(() => {
    if (snapshot) {
      const newMessages = snapshot.docs.map((doc, index, array) => {
        const data = doc.data();
        
        const isUser = data.role === 'user';
        const isFirst = index === 0 || array[index - 1].data().role !== data.role;
        const isLast = index === array.length - 1 || array[index + 1].data().role !== data.role;
        
        let position;
        if (isFirst && isLast) position = 'single';
        else if (isFirst) position = 'first';
        else if (isLast) position = 'last';
        else position = 'normal';

        return {
          id: doc.id,
          ...data,
          position: position,
          message_type: data.audio_url ? 'audio' : 'text',
          content: data.content,
          audio_url: data.audio_url,
          direction: isUser ? 'outgoing' : 'incoming',
          isLastMessage: isLast
        };
      });

      // Si es la carga inicial, marcar como completada y no aplicar streaming
      if (!initialLoadComplete) {
        setInitialLoadComplete(true);
        setMessages(newMessages);
        return;
      }

      // Solo procesar streaming para mensajes que llegan DESPUÉS de la carga inicial
      const currentMessageIds = new Set(messages.map(msg => msg.id));
      const newMessageIds = newMessages.filter(message => !currentMessageIds.has(message.id));

      // Procesar solo los mensajes nuevos del sistema para streaming
      newMessageIds.forEach(message => {
        if (message.role === 'system' && 
            message.content && 
            !processedMessageIds.has(message.id) &&
            !streamingMessages[message.id]) {
          
          simulateStreaming(message.id, message.content);
        }
      });

      setMessages(newMessages);
    }
  }, [snapshot, initialLoadComplete]);

  const handleSendMessage = async (message) => {
    if (!sessionId || !message || isInputDisabled || isSending) return;
    
    setIsSending(true);
    setLocalLoading(true); // Activar loading local inmediatamente
    
    try {
      await addDoc(messagesRef, {
        content: message,
        role: 'user',
        message_type: voiceChatEnabled ? 'audio' : 'text',
        timestamp: serverTimestamp(),
        created_at: serverTimestamp(),
        user_id: userId
      });
      setInputValue('');
    } catch (error) {
      console.error('Error sending message:', error);
      setLocalLoading(false); // Desactivar loading si hay error
    } finally {
      setIsSending(false);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim() && !isSending) {
        handleSendMessage(inputValue.trim());
      }
    }
  };

  const handleSendClick = () => {
    if (inputValue.trim() && !isSending) {
      handleSendMessage(inputValue.trim());
    }
  };

  // Determinar si mostrar el loading indicator
  const shouldShowLoading = currentStatus === 'system_typing' || localLoading;

  const renderMessage = (message) => {
    // Determinar el contenido a mostrar
    let displayContent = message.content || '';
    
    // Si es un mensaje del sistema y está en streaming, mostrar el contenido parcial
    if (message.role === 'system' && streamingMessages[message.id]) {
      displayContent = streamingMessages[message.id];
    }

    // Para mensajes del sistema, usar contenido personalizado para forzar re-renderizado
    if (message.role === 'system') {
      return (
        <Message
          key={`${message.id}-${streamingMessages[message.id] ? 'streaming' : 'complete'}`}
          className="system-message"
          model={{
            message: '', // Contenido vacío en el modelo
            sentTime: message.timestamp?.toDate().toLocaleTimeString(),
            sender: 'Assistant',
            position: message.position,
            message_type: message.message_type,
            direction: 'incoming'
          }}
        >
          <Message.CustomContent>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
              <img
                src={require('../images/chatbot-mensaje-chat-vectorart.png')}
                alt="Bot"
                className="system-avatar"
                style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', background: '#fff' }}
              />
              <div style={{ 
                color: 'white', 
                fontSize: '1rem', 
                lineHeight: '1.4',
                wordWrap: 'break-word',
                background: 'none'
              }}>
                {message.message_type === 'audio' ? (
                  // Renderizar audio para mensajes del sistema
                  audioUrls[message.id] ? (
                    <audio
                      controls
                      style={{ maxWidth: '100%', backgroundColor: '#eee', borderRadius: 6 }}
                      onError={(e) => {
                        console.error('Audio error for message', message.id, ':', e);
                      }}
                    >
                      <source src={audioUrls[message.id]} type="audio/mp3" />
                      Your browser does not support the audio element.
                    </audio>
                  ) : (
                    <div>Loading audio...</div>
                  )
                ) : (
                  // Renderizar texto para mensajes del sistema
                  <>
                    {displayContent}
                    {streamingMessages[message.id] && (
                      <span style={{ 
                        display: 'inline-block',
                        width: '2px',
                        height: '1.2em',
                        backgroundColor: '#4A90E2',
                        animation: 'blink 1s infinite',
                        marginLeft: '2px'
                      }}></span>
                    )}
                  </>
                )}
              </div>
            </div>
          </Message.CustomContent>
        </Message>
      );
    }

    // Para mensajes del usuario, usar el renderizado normal
    return (
      <Message
        key={message.id}
        model={{
          message: displayContent,
          sentTime: message.timestamp?.toDate().toLocaleTimeString(),
          sender: 'You',
          position: message.position,
          message_type: message.message_type,
          direction: message.direction
        }}
      >
        {message.message_type === 'audio' && (
          <Message.CustomContent>
            {audioUrls[message.id] ? (
              <audio
                controls
                style={{ maxWidth: '100%', backgroundColor: '#eee', borderRadius: 6 }}
                onError={(e) => {
                  console.error('Audio error for message', message.id, ':', e);
                }}
              >
                <source src={audioUrls[message.id]} type="audio/mp3" />
                Your browser does not support the audio element.
              </audio>
            ) : (
              <div>Loading audio...</div>
            )}
          </Message.CustomContent>
        )}
      </Message>
    );
  };

  // Auto-resize del textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
    }
  }, [inputValue]);

  return (
    <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px', 
            fontSize: '1rem',
            cursor: 'pointer',
            userSelect: 'none',
            color: 'white'
          }}>
            <input
              type="checkbox"
              checked={voiceChatEnabled}
              onChange={(e) => setVoiceChatEnabled(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            CHAT DE VOZ
          </label>
        </div>
      </Header>
      <div style={{ flex: 1, position: 'relative', minHeight: 0, height: '100%' }}>
        <div style={{ height: '100%' }}>
          <MainContainer>
            <ChatContainer>
              <MessageList
                typingIndicator={shouldShowLoading ? <TypingIndicator /> : null}
                style={{ paddingBottom: 0 }}
              >
                {messages.length === 0 && !loading && !shouldShowLoading && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: 'rgba(255, 255, 255, 0.7)',
                    textAlign: 'center',
                    padding: '20px',
                    fontSize: '1rem',
                    lineHeight: '1.6'
                  }}>
                    No tenés que guardarte todo.<br />¿Querés hablar?
                  </div>
                )}
                {messages.map(renderMessage)}
                <div ref={messagesEndRef} />
              </MessageList>
            </ChatContainer>
          </MainContainer>
        </div>
        <div className="chat-input-footer chat-input-absolute">
                      <textarea
              ref={inputRef}
              className="chat-textarea"
              placeholder={isInputDisabled ? "El sistema está escribiendo..." : isSending ? "Enviando..." : "Escribe tu mensaje..."}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              disabled={isInputDisabled || isSending}
              rows={1}
              style={{ resize: 'none' }}
            />
          <div className="chat-bar">
            <button
              type="button"
              className="chat-mic-btn"
              aria-label="Grabar audio"
              disabled={isInputDisabled || isSending}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 15c1.66 0 3-1.34 3-3V7c0-1.66-1.34-3-3-3s-3 1.34-3 3v5c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.07 2.13 5.64 5 6.32V21h2v-2.68c2.87-.68 5-3.25 5-6.32h-2z" fill="currentColor"/>
              </svg>
            </button>
            <button
              type="button"
              className="chat-send-btn"
              aria-label="Enviar"
              onClick={handleSendClick}
              disabled={!inputValue.trim() || isInputDisabled || isSending}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 12L21 3L12 21L11 13L3 12Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatScreen; 